import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Settings, Send, TestTube, Save, Edit3, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMSConfiguration = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [testSending, setTestSending] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    api_key: '',
    api_secret: '',
    username: '',
    password: '',
    base_url: '',
    sender_id: '',
    is_active: true
  });

  const smsProviders = [
    { 
      value: 'twilio', 
      label: 'Twilio',
      fields: ['api_key', 'api_secret', 'sender_id'],
      baseUrl: 'https://api.twilio.com'
    },
    { 
      value: 'nexmo', 
      label: 'Vonage (Nexmo)',
      fields: ['api_key', 'api_secret', 'sender_id'],
      baseUrl: 'https://rest.nexmo.com'
    },
    { 
      value: 'clickatell', 
      label: 'Clickatell',
      fields: ['api_key', 'username', 'password'],
      baseUrl: 'https://platform.clickatell.com'
    },
    { 
      value: 'textmagic', 
      label: 'TextMagic',
      fields: ['username', 'api_key'],
      baseUrl: 'https://rest.textmagic.com'
    },
    { 
      value: 'plivo', 
      label: 'Plivo',
      fields: ['api_key', 'api_secret'],
      baseUrl: 'https://api.plivo.com'
    },
    { 
      value: 'messagebird', 
      label: 'MessageBird',
      fields: ['api_key'],
      baseUrl: 'https://rest.messagebird.com'
    },
    { 
      value: 'custom', 
      label: 'Custom API',
      fields: ['api_key', 'username', 'password', 'base_url', 'sender_id'],
      baseUrl: ''
    }
  ];

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/sms/configs`);
      setConfigs(response.data);
    } catch (error) {
      console.error("Fetch SMS configs error:", error);
      toast.error("Gabim në ngarkimin e konfigurimit SMS");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingConfig) {
        await axios.put(`${API}/sms/configs/${editingConfig.id}`, formData);
        toast.success("Konfigurimi u përditësua me sukses");
      } else {
        await axios.post(`${API}/sms/configs`, formData);
        toast.success("Konfigurimi u shtua me sukses");
      }
      
      resetForm();
      fetchConfigs();
    } catch (error) {
      toast.error("Gabim në ruajtjen e konfigurimit");
      console.error("SMS config save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData(config);
    setShowAddForm(true);
  };

  const handleDelete = async (configId) => {
    if (!confirm("A jeni i sigurt që doni të fshini këtë konfigurimi?")) return;

    try {
      await axios.delete(`${API}/sms/configs/${configId}`);
      toast.success("Konfigurimi u fshi me sukses");
      fetchConfigs();
    } catch (error) {
      toast.error("Gabim në fshirjen e konfigurimit");
      console.error("SMS config delete error:", error);
    }
  };

  const handleActivate = async (configId) => {
    try {
      await axios.put(`${API}/sms/configs/${configId}/activate`);
      toast.success("Konfigurimi u aktivizua");
      fetchConfigs();
    } catch (error) {
      toast.error("Gabim në aktivizimin e konfigurimit");
      console.error("SMS config activate error:", error);
    }
  };

  const handleTestSMS = async (configId) => {
    const testNumber = prompt("Shkruani numrin e telefonit për test (p.sh. +355691234567):");
    if (!testNumber) return;

    setTestSending(true);
    try {
      await axios.post(`${API}/sms/test`, {
        config_id: configId,
        phone: testNumber,
        message: "Test SMS nga IT Solution - Konfigurimi punon!"
      });
      toast.success("SMS test u dërgua me sukses!");
    } catch (error) {
      toast.error("Gabim në dërgimin e SMS test");
      console.error("SMS test error:", error);
    } finally {
      setTestSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      provider: '',
      api_key: '',
      api_secret: '',
      username: '',
      password: '',
      base_url: '',
      sender_id: '',
      is_active: true
    });
    setEditingConfig(null);
    setShowAddForm(false);
  };

  const getSelectedProvider = () => {
    return smsProviders.find(p => p.value === formData.provider);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-fill base URL when provider is selected
    if (field === 'provider') {
      const provider = smsProviders.find(p => p.value === value);
      if (provider && provider.baseUrl) {
        setFormData(prev => ({ ...prev, base_url: provider.baseUrl }));
      }
    }
  };

  if (loading && configs.length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Konfigurimi i SMS
              </CardTitle>
              <CardDescription>
                Konfiguro dhe menaxho provider-ët e SMS për dërgimin e njoftimeve
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddForm(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Shto Konfigurimi
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Existing Configurations */}
          {configs.length === 0 ? (
            <div className="text-center py-8">
              <Settings className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">Nuk ka konfigurimi SMS të shtuar ende</p>
              <p className="text-slate-400 text-sm">Klikoni "Shto Konfigurimi" për të filluar</p>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-slate-900">{config.name}</h3>
                        <Badge variant={config.is_active ? "default" : "secondary"}>
                          {config.is_active ? "Aktiv" : "Joaktiv"}
                        </Badge>
                        <Badge variant="outline">
                          {smsProviders.find(p => p.value === config.provider)?.label || config.provider}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-slate-600">
                        {config.sender_id && (
                          <div><span className="font-medium">Sender ID:</span> {config.sender_id}</div>
                        )}
                        {config.base_url && (
                          <div><span className="font-medium">API URL:</span> {config.base_url}</div>
                        )}
                        <div><span className="font-medium">Data:</span> {new Date(config.created_at).toLocaleDateString('sq-AL')}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleTestSMS(config.id)}
                        disabled={testSending}
                      >
                        <TestTube className="w-4 h-4 mr-1" />
                        Test
                      </Button>
                      
                      {!config.is_active && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleActivate(config.id)}
                        >
                          Aktivizo
                        </Button>
                      )}
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEdit(config)}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDelete(config.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingConfig ? "Edito Konfigurimin SMS" : "Shto Konfigurimi të Ri SMS"}
            </CardTitle>
            <CardDescription>
              Plotësoni të dhënat e nevojshme për provider-in tuaj të SMS
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Emri i Konfigurimit *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="p.sh. Twilio Production"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="provider">SMS Provider *</Label>
                  <Select value={formData.provider} onValueChange={(value) => handleChange('provider', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Zgjidh provider-in" />
                    </SelectTrigger>
                    <SelectContent>
                      {smsProviders.map((provider) => (
                        <SelectItem key={provider.value} value={provider.value}>
                          {provider.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dynamic fields based on selected provider */}
              {getSelectedProvider() && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-slate-700 border-b pb-2">
                    Parametrat e {getSelectedProvider().label}
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getSelectedProvider().fields.includes('api_key') && (
                      <div>
                        <Label htmlFor="api_key">API Key *</Label>
                        <Input
                          id="api_key"
                          type="password"
                          value={formData.api_key}
                          onChange={(e) => handleChange('api_key', e.target.value)}
                          placeholder="API Key nga provider-i"
                          required
                        />
                      </div>
                    )}
                    
                    {getSelectedProvider().fields.includes('api_secret') && (
                      <div>
                        <Label htmlFor="api_secret">API Secret</Label>
                        <Input
                          id="api_secret"
                          type="password"
                          value={formData.api_secret}
                          onChange={(e) => handleChange('api_secret', e.target.value)}
                          placeholder="API Secret nga provider-i"
                        />
                      </div>
                    )}
                    
                    {getSelectedProvider().fields.includes('username') && (
                      <div>
                        <Label htmlFor="username">Username</Label>
                        <Input
                          id="username"
                          value={formData.username}
                          onChange={(e) => handleChange('username', e.target.value)}
                          placeholder="Username për API"
                        />
                      </div>
                    )}
                    
                    {getSelectedProvider().fields.includes('password') && (
                      <div>
                        <Label htmlFor="password">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleChange('password', e.target.value)}
                          placeholder="Password për API"
                        />
                      </div>
                    )}
                    
                    {getSelectedProvider().fields.includes('sender_id') && (
                      <div>
                        <Label htmlFor="sender_id">Sender ID</Label>
                        <Input
                          id="sender_id"
                          value={formData.sender_id}
                          onChange={(e) => handleChange('sender_id', e.target.value)}
                          placeholder="p.sh. IT Solution"
                        />
                      </div>
                    )}
                    
                    {getSelectedProvider().fields.includes('base_url') && (
                      <div>
                        <Label htmlFor="base_url">Base URL</Label>
                        <Input
                          id="base_url"
                          value={formData.base_url}
                          onChange={(e) => handleChange('base_url', e.target.value)}
                          placeholder="https://api.example.com"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4 border-t">
                <Button type="submit" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  {editingConfig ? "Përditëso" : "Ruaj"} Konfigurimin
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Anulo
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SMSConfiguration;